import {
  type ColumnRenderer,
  type ExplorerDataProvider,
  FilterTypes,
  ImgElement,
  NavigationExplorer,
  createInMemoryDataProvider,
  findAnywhereInStringCaseInsensitive,
  findExact,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import {
  ContainerImagesDocument,
  type ContainerImagesQuery,
  type ContainerImagesQueryVariables,
} from 'generated/hosting-service';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { type ReactNode, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getDeploymentStateIconLabel } from '../common/utils';
import classes from './ContainerRegistryExplorer.module.scss';
import type { ContainerImage } from './ContainerRegistryExplorer.types';

const UsedInActiveDeploymentStatusRenderer: ColumnRenderer<ContainerImage> = (
  _value,
  data,
): ReactNode => {
  const { label, icon } = getDeploymentStateIconLabel(
    data.usedInActiveDeployment,
  );
  return (
    <div className={classes.status_container}>
      <ImgElement
        src={icon}
        style={{ width: '20px', height: '20px' }}
        decorative={true}
      />
      <div className={classes.status}>{label}</div>
    </div>
  );
};

export const ContainerRegistryExplorer: React.FC = (props) => {
  const { envId, customizableServiceId } = useParams<{
    envId: string;
    customizableServiceId: string;
  }>();
  const [filteredData, setFilteredData] = useState<ContainerImage[]>([]);

  // Data provider
  const dataProvider: ExplorerDataProvider<ContainerImage> = useMemo(() => {
    return {
      loadData: async ({ pagingInformation, filters, sorting }) => {
        if (filteredData.length === 0) {
          const envApolloClient = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
          );
          const result = await envApolloClient?.query<
            ContainerImagesQuery,
            ContainerImagesQueryVariables
          >({
            query: ContainerImagesDocument,
            variables: {
              id: customizableServiceId,
            },
            fetchPolicy: 'network-only',
          });
          const containerImageData: React.SetStateAction<
            ContainerImage[] | undefined
          > = [];
          result?.data?.serviceDefinition?.repositoryTags.map((item) => {
            const imageData = {
              containerImage: item.containerImageVersion,
              usedInActiveDeployment:
                result?.data?.serviceDefinition?.serviceDeployments.nodes[0] !==
                  undefined &&
                item.tag ===
                  result?.data?.serviceDefinition?.serviceDeployments.nodes[0]
                    .containerImageVersion,
            };
            containerImageData.push(imageData);
            setFilteredData(containerImageData);
          });
          return {
            totalCount: containerImageData.length,
            filteredCount: containerImageData.length,
            data: containerImageData,
            pagingInformation: 1,
            hasMoreData: false,
          };
        } else {
          const actualProvider = createInMemoryDataProvider<ContainerImage>(
            filteredData ?? [],
            {
              filterFunctions: {
                containerImage: findAnywhereInStringCaseInsensitive,
                usedInActiveDeployment: findExact,
              },
            },
          );
          // Increase page count
          const currentPage = (pagingInformation as number) ?? 0;

          // Use the in memory provider to do the heavy lifting
          const { totalCount, filteredCount, data } =
            await actualProvider.loadData({
              pagingInformation,
              filters,
              sorting,
            });

          // Apply paging to the results
          const pageSize = 20;
          return {
            totalCount: totalCount,
            filteredCount: filteredCount,
            data: data?.slice(
              pageSize * currentPage,
              pageSize * (currentPage + 1),
            ),
            pagingInformation: currentPage + 1,
            hasMoreData: currentPage < 9,
          };
        }
      },
    };
  }, [customizableServiceId, envId, filteredData]);

  return (
    <NavigationExplorer<ContainerImage>
      {...props}
      stationKey={'ContainerImagesExplorer'}
      title="Container Images"
      columns={[
        {
          label: 'Container Image Version',
          propertyName: 'containerImage',
          size: '2fr',
        },
        {
          label: 'Deployment State',
          propertyName: 'usedInActiveDeployment',
          render: UsedInActiveDeploymentStatusRenderer,
        },
      ]}
      dataProvider={dataProvider}
      filterOptions={[
        {
          label: 'Container Image Version',
          property: 'containerImage',
          type: FilterTypes.FreeText,
        },
        {
          label: 'Deployment State',
          property: 'usedInActiveDeployment',
          type: FilterTypes.Options,
          options: [
            {
              label: 'Deployed',
              value: true,
            },
            {
              label: 'Not Deployed',
              value: false,
            },
          ],
        },
      ]}
    />
  );
};

export const ContainerRegistryExplorerCrumb: BreadcrumbResolver = () =>
  'Container Images';
